<template>
    <v-dialog
        v-model="show"
        max-width="900px"
        persistent
    >
        <v-card>
            <v-card-title class="text-h5 lighten-2" style="border-bottom: solid 1px var(--color__cinza)">
                Impervious Surface Coverage Calculation
            </v-card-title>

            <v-card-text class="pt-4">

                <!-- Project Info (read-only) -->
                <v-row>
                    <v-col cols="3">
                        <div class="info-field">
                            <span class="info-label">Project</span>
                            <label>{{ item ? item.idFormatted : '' }}</label>
                        </div>
                    </v-col>
                    <v-col cols="3">
                        <div class="info-field">
                            <span class="info-label">Owner</span>
                            <label>{{ item ? item.customerName : '' }}</label>
                        </div>
                    </v-col>
                    <v-col cols="3">
                        <div class="info-field">
                            <span class="info-label">Site Address</span>
                            <label>{{ item ? item.customerAddress : '' }}</label>
                        </div>
                    </v-col>
                    <v-col cols="3">
                        <div class="info-field">
                            <span class="info-label">City</span>
                            <label>{{ item ? item.customerCity : '' }}</label>
                        </div>
                    </v-col>
                </v-row>

                <v-divider class="my-3" />

                <!-- Section A -->
                <div class="section-title mb-2">A — Total Square Foot Area of Entire Lot</div>
                <v-row dense>
                    <v-col cols="6">
                        <v-text-field
                            label="Total Lot Area (sq ft)"
                            v-model.number="form.totalSquareFootLot"
                            outlined dense type="number" min="0"
                        />
                    </v-col>
                </v-row>

                <v-divider class="my-3" />

                <!-- Section B -->
                <div class="section-title mb-2">B — Existing Impervious Square Footage</div>
                <v-row dense>
                    <v-col cols="6">
                        <v-text-field label="House" v-model.number="form.existingHouse" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field label="Driveway" v-model.number="form.existingDriveway" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field label="Walkway" v-model.number="form.existingWalkway" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field label="A/C and Equipment Pad" v-model.number="form.existingACEquipmentPad" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field label="Existing Patio / Screen Room / Pool" v-model.number="form.existingPatio" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field label="Other" v-model.number="form.existingOther" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            label="Total Existing (B)"
                            :value="totalExisting.toFixed(2)"
                            outlined dense readonly
                            background-color="#e8f4f8"
                        />
                    </v-col>
                </v-row>

                <v-divider class="my-3" />

                <!-- Section C -->
                <div class="section-title mb-2">C — New Impervious Square Footage</div>
                <v-row dense>
                    <v-col cols="8">
                        <v-text-field label="Description 1" v-model="form.newDesc1" outlined dense />
                    </v-col>
                    <v-col cols="4">
                        <v-text-field label="Value" v-model.number="form.newValue1" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="8">
                        <v-text-field label="Description 2" v-model="form.newDesc2" outlined dense />
                    </v-col>
                    <v-col cols="4">
                        <v-text-field label="Value" v-model.number="form.newValue2" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="8">
                        <v-text-field label="Description 3" v-model="form.newDesc3" outlined dense />
                    </v-col>
                    <v-col cols="4">
                        <v-text-field label="Value" v-model.number="form.newValue3" outlined dense type="number" min="0" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            label="Total New (C)"
                            :value="totalNew.toFixed(2)"
                            outlined dense readonly
                            background-color="#e8f4f8"
                        />
                    </v-col>
                </v-row>

                <v-divider class="my-3" />

                <!-- Calculations -->
                <div class="section-title mb-2">Calculations</div>
                <v-row dense>
                    <v-col cols="6">
                        <v-text-field
                            label="D = B + C (Total Impervious)"
                            :value="totalImpervious.toFixed(2)"
                            outlined dense readonly
                            background-color="#e8f4f8"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            label="Coverage % (D ÷ A × 100)"
                            :value="coveragePercent"
                            outlined dense readonly
                            :background-color="coverageExceeded ? '#ffcccc' : '#e8f4f8'"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            label="Maximum Allowed (Cooper City)"
                            value="55.00%"
                            outlined dense readonly
                            background-color="#e8f4f8"
                        />
                    </v-col>
                </v-row>

            </v-card-text>

            <v-card-actions>
				<div style="width: 100%; text-align: center;">
                    <v-btn 
                        class="mx-2 dialogButtonCancel v-btn-large"
                        @click="$emit('close')"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        class="mx-2 v-btn-large"
                        @click="save"
                        :loading="saving"
                    >
                        <v-icon left>mdi-content-save-outline</v-icon>Save
                    </v-btn>
                    <v-btn
                        class="mx-2 v-btn-large"
                        dark
                        @click="exportPdf"
                        :loading="exporting"
                    >
                        <v-icon left>mdi-file-pdf-box</v-icon>Export PDF
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import Helpers from '@/utilities/Helpers';
    import { URL_LOT_COVERAGE_PDF, PDF_NAMES } from '@/utilities/Enums';

    export default {
        name: 'LotCoverage',

        mixins: [Helpers],

        props: {
            idServiceProject: {
                type: Number,
                required: true
            },
            show: {
                type: Boolean,
                default: false
            },
            item: {
                type: Object,
                default: null
            }
        },

        data: () => ({
            saving: false,
            exporting: false,
            menuDate: false,

            form: {
                idLotCoverage: 0,
                idServiceProject: 0,
                totalSquareFootLot: 0,
                existingHouse: 0,
                existingDriveway: 0,
                existingWalkway: 0,
                existingACEquipmentPad: 0,
                existingPatio: 0,
                existingOther: 0,
                newDesc1: '',
                newValue1: 0,
                newDesc2: '',
                newValue2: 0,
                newDesc3: '',
                newValue3: 0,
                completedBy: '',
                signatureDate: null,
            }
        }),

        computed: {
            totalExisting() {
                return (this.form.existingHouse || 0) +
                       (this.form.existingDriveway || 0) +
                       (this.form.existingWalkway || 0) +
                       (this.form.existingACEquipmentPad || 0) +
                       (this.form.existingPatio || 0) +
                       (this.form.existingOther || 0);
            },
            totalNew() {
                return (this.form.newValue1 || 0) +
                       (this.form.newValue2 || 0) +
                       (this.form.newValue3 || 0);
            },
            totalImpervious() {
                return this.totalExisting + this.totalNew;
            },
            coveragePercent() {
                if (!this.form.totalSquareFootLot || this.form.totalSquareFootLot <= 0) return '0.00%';
                const pct = (this.totalImpervious / this.form.totalSquareFootLot) * 100;
                return pct.toFixed(2) + '%';
            },
            coverageExceeded() {
                if (!this.form.totalSquareFootLot || this.form.totalSquareFootLot <= 0) return false;
                const pct = (this.totalImpervious / this.form.totalSquareFootLot) * 100;
                return pct > 55;
            },
            signatureDateFormatted() {
                if (!this.form.signatureDate) return '';
                const [y, m, d] = this.form.signatureDate.split('-');
                return `${m}/${d}/${y}`;
            }
        },

        watch: {
            show(val) {
                if (val && this.idServiceProject > 0) {
                    this.loadData();
                }
            }
        },

        methods: {
            async loadData() {
                this.form = {
                    idLotCoverage: 0,
                    idServiceProject: 0,
                    totalSquareFootLot: 0,
                    existingHouse: 0,
                    existingDriveway: 0,
                    existingWalkway: 0,
                    existingACEquipmentPad: 0,
                    existingPatio: 0,
                    existingOther: 0,
                    newDesc1: '',
                    newValue1: 0,
                    newDesc2: '',
                    newValue2: 0,
                    newDesc3: '',
                    newValue3: 0,
                    completedBy: '',
                    signatureDate: null,
                };

                this.showLoading();
                try {
                    const data = await this.$store.dispatch('lotCoverageModule/GetByServiceProject', this.idServiceProject);
                    if (data && data.idLotCoverage) {
                        this.form = {
                            idLotCoverage: data.idLotCoverage,
                            idServiceProject: data.idServiceProject,
                            totalSquareFootLot: data.totalSquareFootLot || 0,
                            existingHouse: data.existingHouse || 0,
                            existingDriveway: data.existingDriveway || 0,
                            existingWalkway: data.existingWalkway || 0,
                            existingACEquipmentPad: data.existingACEquipmentPad || 0,
                            existingPatio: data.existingPatio || 0,
                            existingOther: data.existingOther || 0,
                            newDesc1: data.newDesc1 || '',
                            newValue1: data.newValue1 || 0,
                            newDesc2: data.newDesc2 || '',
                            newValue2: data.newValue2 || 0,
                            newDesc3: data.newDesc3 || '',
                            newValue3: data.newValue3 || 0,
                            completedBy: data.completedBy || '',
                            signatureDate: data.signatureDate ? data.signatureDate.substring(0, 10) : null,
                        };
                    }
                } finally {
                    this.hideLoading();
                }
            },

            async save() {
                this.saving = true;
                try {
                    const request = {
                        IDLotCoverage: this.form.idLotCoverage,
                        IDServiceProject: this.idServiceProject,
                        TotalSquareFootLot: this.form.totalSquareFootLot || 0,
                        ExistingHouse: this.form.existingHouse || 0,
                        ExistingDriveway: this.form.existingDriveway || 0,
                        ExistingWalkway: this.form.existingWalkway || 0,
                        ExistingACEquipmentPad: this.form.existingACEquipmentPad || 0,
                        ExistingPatio: this.form.existingPatio || 0,
                        ExistingOther: this.form.existingOther || 0,
                        NewDesc1: this.form.newDesc1,
                        NewValue1: this.form.newValue1 || 0,
                        NewDesc2: this.form.newDesc2,
                        NewValue2: this.form.newValue2 || 0,
                        NewDesc3: this.form.newDesc3,
                        NewValue3: this.form.newValue3 || 0,
                        CompletedBy: this.form.completedBy,
                        SignatureDate: this.form.signatureDate || null,
                    };
                    const id = await this.$store.dispatch('lotCoverageModule/Save', request);
                    if (id) {
                        this.form.idLotCoverage = id;
                        this.showToast("success", "Success!", 'Lot coverage saved successfully!');
                    }
                } catch (ex) {
                    this.showToast("error", "Error!", 'Error saving lot coverage.');
                } finally {
                    this.saving = false;
                }
            },

            async exportPdf() {
                this.exporting = true;
                try {
                    await this.downloadFile(URL_LOT_COVERAGE_PDF, this.idServiceProject, 'application/pdf', PDF_NAMES.LOT_COVERAGE);
                } finally {
                    this.exporting = false;
                }
            },
        }
    }
</script>

<style scoped>
    .section-title {
        font-size: 13px;
        font-weight: bold;
        color: #14435B;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .info-field {
        display: flex;
        flex-direction: column;
        margin-bottom: 12px;
    }

    .info-label {
        font-size: 11px;
        color: rgba(0, 0, 0, 0.6);
        margin-bottom: 2px;
    }

    .info-field label {
        font-size: 14px;
        color: rgba(0, 0, 0, 0.87);
    }
</style>
